import type { GroupSubscription } from "@/models/subscription";
import type {
  SubscriptionGroup,
  SubscriptionGroupAnalytics,
  SubscriptionGroupInvite,
  ValidateGroupInviteCodeResponse,
} from "@/models/subscriptionGroup";
import { API_BASE, type APIResponse, callApi } from "@/services/api";

interface UpsertInviteResponse {
  message: string;
  // Only the invites the request actually created — emails that already
  // belonged to an account are omitted, which is how the invite modal works
  // out which addresses to report back as "already has an account". Optional
  // because not every upsert response carries it (and older responses did
  // not), so callers must guard before using it.
  invitesCreated?: { email: string }[];
  // Addresses skipped because they already belong to an account.
  emailsTaken?: string[];
  members: {
    data: SubscriptionGroupInvite[];
  };
}

interface MemberListLazyLoadingResponse {
  members: {
    data: SubscriptionGroupInvite["members"]["data"];
    meta: { numberOfItems: number };
  };
  activeCount?: number;
  pendingCount?: number;
}

export async function getSubscriptionGroup(): Promise<
  APIResponse<SubscriptionGroup> | undefined
> {
  try {
    const resp = await callApi<SubscriptionGroup>(
      `${API_BASE}/subscription_groups`,
    );
    return resp;
  } catch {
    return undefined;
  }
}
export async function getSubscriptionGroupAdmin(): Promise<
  APIResponse<GroupSubscription> | undefined
> {
  try {
    const resp = await callApi<GroupSubscription>(
      `${API_BASE}/subscription_groups/subscription`,
    );
    return resp;
  } catch {
    return undefined;
  }
}

export async function getSubscriptionGroupAnalytics(): Promise<
  APIResponse<SubscriptionGroupAnalytics> | undefined
> {
  try {
    const resp = await callApi<SubscriptionGroupAnalytics>(
      `${API_BASE}/subscription_groups/analytics`,
    );
    return resp;
  } catch {
    return undefined;
  }
}

export async function updateSubscriptionGroup(opts: {
  name: string;
  industry: string;
  // Both optional because the two real callers send different subsets: the
  // team details form sends companySize, the post-payment org signup flow
  // (which has no company-size input) sends location instead.
  companySize?: string;
  location?: string;
}): Promise<APIResponse<SubscriptionGroup> | undefined> {
  try {
    const resp = await callApi<SubscriptionGroup>(
      `${API_BASE}/subscription_groups/update`,
      {
        method: "PUT",
        body: opts,
      },
    );
    return resp;
  } catch {
    return undefined;
  }
}

export async function getSubscriptionGroupInvites(): Promise<
  | APIResponse<{
      members: {
        data: SubscriptionGroupInvite[];
      };
    }>
  | undefined
> {
  try {
    const resp = await callApi<{
      members: {
        data: SubscriptionGroupInvite[];
      };
    }>(`${API_BASE}/subscription_groups/members`);
    return resp;
  } catch {
    return undefined;
  }
}

export async function getMemberListLazyLoading(opts: {
  perPage: number;
  page: number;
}): Promise<APIResponse<MemberListLazyLoadingResponse> | undefined> {
  try {
    const resp = await callApi<MemberListLazyLoadingResponse>(
      `${API_BASE}/subscription_groups/members?per_page=${opts.perPage}&page=${opts.page}`,
    );
    return resp;
  } catch {
    return undefined;
  }
}

// The request key is `emails` (serialised as `emails`), not `email`. The
// endpoint took a single `email` string when it was first wired up in 2021;
// it was converted to a bulk endpoint the same month and every caller has
// sent an `emails` array ever since. Only this annotation was left behind on
// the old singular name, and nothing caught it because the hook wrapper in
// front of it was typed `any`.
export async function createSubscriptionGroupInvite(opts: {
  emails: string[];
}): Promise<APIResponse<UpsertInviteResponse> | undefined> {
  try {
    const resp = await callApi<UpsertInviteResponse>(
      `${API_BASE}/subscription_groups/create-invite`,
      {
        method: "POST",
        body: opts,
      },
    );
    return resp;
  } catch {
    return undefined;
  }
}

export async function deleteSubscriptionGroupInvite(opts: {
  inviteIds: number[];
}): Promise<APIResponse<UpsertInviteResponse> | undefined> {
  try {
    const resp = await callApi<UpsertInviteResponse>(
      `${API_BASE}/subscription_groups/delete-members`,
      {
        method: "DELETE",
        body: opts,
      },
    );
    return resp;
  } catch {
    return undefined;
  }
}

export async function resendSubscriptionGroupInvite(opts: {
  inviteIds: number[];
}): Promise<APIResponse<UpsertInviteResponse> | undefined> {
  try {
    const resp = await callApi<UpsertInviteResponse>(
      `${API_BASE}/subscription_groups/resend-invites`,
      {
        method: "POST",
        body: opts,
      },
    );
    return resp;
  } catch {
    return undefined;
  }
}

export async function validateGroupInviteCode(
  inviteCode: string,
): Promise<APIResponse<ValidateGroupInviteCodeResponse> | undefined> {
  try {
    const resp = await callApi<ValidateGroupInviteCodeResponse>(
      `${API_BASE}/subscription_groups/validate?invite_code=${inviteCode}`,
    );
    return resp;
  } catch {
    return undefined;
  }
}

export async function manageAdminSubscriptionGroups(opts: {
  // A numeric user id, like every other id in this file — it only ever gets
  // interpolated into the URL path below, so this is a naming correction with
  // no change to the request.
  memberId: number;
  actionType: string;
}): Promise<APIResponse<void> | undefined> {
  try {
    const resp = await callApi<void>(
      `${API_BASE}/subscription_groups/manage-admin/${opts.memberId}`,
      {
        method: "POST",
        body: { actionType: opts.actionType },
      },
    );
    return resp;
  } catch {
    return undefined;
  }
}
